import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useEffect, useRef } from 'react';
import type { LatLng } from '../lib/geo';
import { useAppStore, type Which } from '../state/store';
import { shadeColor } from './colors';

// Basemap CARTO (gratuita per uso leggero, attribuzione inclusa): pulita e
// neutra, così il percorso colorato risalta — stile Apple Maps.
const TILES_LIGHT = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILES_DARK = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>';

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)');
const isDesktop = () => window.matchMedia('(min-width: 900px)').matches;

/** Marker di partenza: cerchio bianco con anello scuro, come l'origine in Google Maps. */
function originIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: '<div class="mk-origin"></div>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

/** Marker di arrivo: il pin rosso di Google Maps. */
function destIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<svg class="mk-dest" viewBox="0 0 32 44" width="32" height="44" aria-hidden="true">
      <path d="M16 1.5C8 1.5 1.5 8 1.5 16c0 10.2 14.5 26.5 14.5 26.5S30.5 26.2 30.5 16C30.5 8 24 1.5 16 1.5z" fill="#EA4335" stroke="#fff" stroke-width="1.6"/>
      <circle cx="16" cy="16" r="5.5" fill="#8b1d12"/></svg>`,
    iconSize: [32, 44],
    iconAnchor: [16, 43],
  });
}

/** Posizione dell'utente: puntino blu con alone pulsante. */
function userIcon(): L.DivIcon {
  return L.divIcon({
    className: '',
    html: '<div class="mk-user"><div class="mk-user__halo"></div><div class="mk-user__dot"></div></div>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });
}

const toLL = (p: L.LatLng): LatLng => ({ lat: p.lat, lng: p.lng });
const toArr = (c: LatLng): [number, number] => [c.lat, c.lng];

/** Padding del fitBounds: lascia libero lo spazio coperto da sheet (mobile) o pannello (desktop). */
function fitOptions(): L.FitBoundsOptions {
  if (isDesktop()) return { paddingTopLeft: [448, 32], paddingBottomRight: [32, 32] };
  const sheetH =
    parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sheet-h')) || 0;
  // sopra: la scheda partenza/arrivo flottante; sotto: il bottom sheet
  return { paddingTopLeft: [20, 150], paddingBottomRight: [20, sheetH + 24] };
}

export function MapView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tilesRef = useRef<L.TileLayer | null>(null);
  const markersRef = useRef<Partial<Record<Which, L.Marker>>>({});
  const routeLayerRef = useRef<L.LayerGroup | null>(null);
  const heatLayerRef = useRef<L.LayerGroup | null>(null);
  const canvasRef = useRef<L.Canvas | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);
  const accuracyRef = useRef<L.Circle | null>(null);
  const lastFitKeyRef = useRef('');
  const zoomedToUserRef = useRef(false);

  const a = useAppStore((s) => s.a);
  const b = useAppStore((s) => s.b);
  const plan = useAppStore((s) => s.plan);
  const status = useAppStore((s) => s.status);
  const pickTarget = useAppStore((s) => s.pickTarget);
  const heatmapOn = useAppStore((s) => s.heatmapOn);
  const heatmap = useAppStore((s) => s.heatmap);
  const navActive = useAppStore((s) => s.navActive);
  const userPos = useAppStore((s) => s.userPos);
  const followUser = useAppStore((s) => s.followUser);

  // ---- inizializzazione ----
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const map = L.map(el, {
      zoomControl: false,
      attributionControl: false,
      zoomSnap: 0.5,
    }).setView([45.4642, 9.19], 14);
    L.control
      .attribution({ position: isDesktop() ? 'bottomright' : 'bottomleft', prefix: false })
      .addTo(map);
    const mql = darkQuery();
    tilesRef.current = L.tileLayer(mql.matches ? TILES_DARK : TILES_LIGHT, {
      maxZoom: 20,
      subdomains: 'abcd',
      attribution: ATTRIBUTION,
    }).addTo(map);
    const onTheme = (e: MediaQueryListEvent) =>
      tilesRef.current?.setUrl(e.matches ? TILES_DARK : TILES_LIGHT);
    mql.addEventListener('change', onTheme);

    canvasRef.current = L.canvas({ padding: 0.5 });
    heatLayerRef.current = L.layerGroup().addTo(map);
    routeLayerRef.current = L.layerGroup().addTo(map);

    map.on('click', (e: L.LeafletMouseEvent) => {
      useAppStore.getState().handleMapClick(toLL(e.latlng));
    });
    map.on('dragstart', () => {
      const s = useAppStore.getState();
      if (s.navActive && s.followUser) s.setFollowUser(false);
    });
    mapRef.current = map;

    // centra sulla posizione dell'utente, se disponibile (senza impostare punti)
    if (!useAppStore.getState().a && !window.location.hash && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (!useAppStore.getState().a) {
            map.setView([pos.coords.latitude, pos.coords.longitude], 15);
          }
        },
        () => undefined,
        { maximumAge: 600_000, timeout: 5_000 },
      );
    }

    return () => {
      mql.removeEventListener('change', onTheme);
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
      routeLayerRef.current = null;
      heatLayerRef.current = null;
      userMarkerRef.current = null;
      accuracyRef.current = null;
    };
  }, []);

  // ---- marker A/B ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const specs: Array<{ which: Which; sel: typeof a; icon: () => L.DivIcon }> = [
      { which: 'a', sel: a, icon: originIcon },
      { which: 'b', sel: b, icon: destIcon },
    ];
    for (const { which, sel, icon } of specs) {
      const existing = markersRef.current[which];
      if (!sel) {
        existing?.remove();
        delete markersRef.current[which];
        continue;
      }
      if (existing) {
        existing.setLatLng(toArr(sel.ll));
      } else {
        const m = L.marker(toArr(sel.ll), { icon: icon(), draggable: true, keyboard: false }).addTo(
          map,
        );
        m.on('dragend', () => useAppStore.getState().setPoint(which, toLL(m.getLatLng())));
        markersRef.current[which] = m;
      }
    }
  }, [a, b]);

  // ---- percorso ----
  useEffect(() => {
    const layer = routeLayerRef.current;
    const map = mapRef.current;
    if (!layer || !map) return;
    layer.clearLayers();
    if (!plan || status !== 'done') return;

    const chosen = plan.chosen;
    const dark = darkQuery().matches;
    const shortestDiffers = Math.abs(plan.shortest.lengthM - chosen.lengthM) > 1;

    if (shortestDiffers) {
      L.polyline(plan.shortest.coords.map(toArr), {
        color: dark ? '#8e8e93' : '#6e6e73',
        weight: 4,
        opacity: 0.6,
        dashArray: '4 8',
        interactive: false,
      }).addTo(layer);
    }
    // casing + segmenti colorati per ombra
    L.polyline(chosen.coords.map(toArr), {
      color: dark ? '#1c1c1e' : '#ffffff',
      weight: 11,
      opacity: 0.95,
      interactive: false,
      lineCap: 'round',
    }).addTo(layer);
    for (const seg of chosen.segments) {
      L.polyline(seg.coords.map(toArr), {
        color: shadeColor(seg.shade),
        weight: 6.5,
        opacity: 1,
        interactive: false,
        lineCap: 'round',
      }).addTo(layer);
    }

    const fitKey = [plan.snappedStart, plan.snappedEnd]
      .map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
      .join('|');
    if (fitKey !== lastFitKeyRef.current && !useAppStore.getState().navActive) {
      lastFitKeyRef.current = fitKey;
      // il fit va fatto dopo che lo sheet ha comunicato la sua altezza
      requestAnimationFrame(() => {
        map.fitBounds(L.latLngBounds(chosen.coords.map(toArr)), {
          ...fitOptions(),
          animate: true,
          maxZoom: 17,
        });
      });
    }
  }, [plan, status]);

  // ---- heatmap dell'ombra ----
  useEffect(() => {
    const layer = heatLayerRef.current;
    const canvas = canvasRef.current;
    if (!layer || !canvas) return;
    layer.clearLayers();
    if (!heatmapOn || !heatmap) return;
    for (const e of heatmap) {
      L.polyline(e.coords.map(toArr), {
        renderer: canvas,
        color: shadeColor(e.shade),
        weight: 3.5,
        opacity: 0.85,
        interactive: false,
      }).addTo(layer);
    }
  }, [heatmapOn, heatmap]);

  // ---- posizione dell'utente ----
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!userPos || !navActive) {
      userMarkerRef.current?.remove();
      accuracyRef.current?.remove();
      userMarkerRef.current = null;
      accuracyRef.current = null;
      zoomedToUserRef.current = false;
      return;
    }
    const pos = toArr(userPos.ll);
    if (!userMarkerRef.current) {
      userMarkerRef.current = L.marker(pos, {
        icon: userIcon(),
        interactive: false,
        zIndexOffset: 1000,
      }).addTo(map);
      accuracyRef.current = L.circle(pos, {
        radius: userPos.accuracyM,
        color: '#1a73e8',
        weight: 1,
        opacity: 0.35,
        fillColor: '#1a73e8',
        fillOpacity: 0.1,
        interactive: false,
      }).addTo(map);
    } else {
      userMarkerRef.current.setLatLng(pos);
      accuracyRef.current?.setLatLng(pos).setRadius(userPos.accuracyM);
    }
    if (followUser) {
      if (!zoomedToUserRef.current) {
        zoomedToUserRef.current = true;
        map.setView(pos, Math.max(map.getZoom(), 17), { animate: true });
      } else {
        map.panTo(pos, { animate: true, duration: 0.6 });
      }
    }
  }, [userPos, navActive, followUser]);

  return (
    <div
      ref={containerRef}
      className={`map${pickTarget ? ' map--picking' : ''}`}
      aria-label="Mappa"
    />
  );
}
